import './styles/Timer.scss';
import ClockIcon from '@/features/Icons/ClockIcon.tsx';
import React from 'react';
import { GenericIcon } from '@/features/GenericIcon/GenericIcon';
import { getIconProductType } from '@/features/GenericIcon/types';
import { useTimers } from '@/features/Timer/useTimers.ts';
import { useAppOrientation } from '@/lostHooks/orientation/useOrientation';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { SHOWOFF_ELEMENTS } from '@/features/Showoff/types';

/**
 * Timer component that displays current time, session time, and game time
 */
export const Timer: React.FC<TimerProps> = ({ menu, isSWJ }) => {
  const orientation = useAppOrientation();
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );
  const geolocationSuccess = useStore(
    (s) => s.geolocationStatus.geolocationSuccess,
  );

  const { currentTime, sessionTime, gameTime } = useStore(
    useShallow((s) => ({
      currentTime: s.currentTime,
      sessionTime: s.sessionTime,
      gameTime: s.gameTime,
    })),
  );

  const gameWindowOpen = useStore((s) => s.gameWindowContext.gameWindowOpen);
  const hideLogo = !isSWJ && orientation === 'portrait';
  // Initialize server time

  // Initialize timers only once pre-checks are complete (active session + geolocation success).
  const preChecksComplete = hasActiveSession && geolocationSuccess;
  useTimers(preChecksComplete);

  return (
    <div
      className={`timer-container ${menu ? 'menu' : ''} ${orientation}`}
      data-showoff={SHOWOFF_ELEMENTS.TIMERS}
      data-showoff-notification="false"
    >
      {gameWindowOpen ? (
        <div className={`icon-container ${orientation}`}>
          <ClockIcon />
        </div>
      ) : (
        !hideLogo && (
          <div className={`icon-container ${orientation}`}>
            <GenericIcon type="logo" product={getIconProductType()} />
          </div>
        )
      )}
      <div
        className={`timer-column ${orientation}`}
        data-testid="timers-container"
      >
        <div className="timer-row">
          <span className="timer-label" data-testid="current-timer-label">
            Time:
          </span>
          <span className="timer-value" data-testid="current-timer-value">
            {currentTime}
          </span>
        </div>
        <div className="timer-row">
          <span className="timer-label" data-testid="session-timer-label">
            Session:
          </span>
          <span className="timer-value" data-testid="session-timer-value">
            {sessionTime}
          </span>
        </div>
        <div className="timer-row">
          <span className="timer-label" data-testid="game-timer-label">
            Game:
          </span>
          <span className="timer-value" data-testid="game-timer-value">
            {gameTime}
          </span>
        </div>
      </div>
    </div>
  );
};

interface TimerProps {
  isPortrait?: boolean;
  menu?: boolean;
  isLandscape?: boolean;
  isSWJ?: boolean;
}
